import { MouseEvent } from 'react';

import { PlusIcon } from '@braskai/icons';
import { useNavigate } from 'react-router-dom';

import { type NavigationItemProps } from '../NavigationItem';
import {
  SHintTooltip,
  SIDEBAR_ITEM_ACTION_CLASS,
  SItem,
  SItemAction,
  SItemButton,
  SItemIcon,
  SItemTitle,
} from './styled';

const ACTION_TOOLTIP_DELAY_MS = 1000;
const ACTION_TOOLTIP_POPPER_PROPS = {
  modifiers: [{ name: 'offset', options: { offset: [-6, 11] } }],
};

type DesktopSidebarItemProps = {
  isOpen?: boolean;
  item: NavigationItemProps;
  variant: 'primary' | 'secondary';
};

export const DesktopSidebarItem = ({ isOpen = true, item, variant }: DesktopSidebarItemProps) => {
  const navigate = useNavigate();
  const { title, action, dataTestId, icon, navigationState, path, selected = false, spotlightAnchor, onClick } = item;

  const handleClick = () => {
    if (path) {
      navigate(path, navigationState && { state: navigationState });
    }

    onClick?.();
  };

  const handleActionClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    action?.onClick();
  };

  return (
    <SItem $selected={selected} $variant={variant} data-spotlight-anchor={spotlightAnchor}>
      <SItemButton
        title={isOpen ? undefined : title}
        $variant={variant}
        aria-current={selected ? 'page' : undefined}
        data-testid={dataTestId}
        onClick={handleClick}
      >
        {icon && (
          <SItemIcon $variant={variant} aria-hidden>
            {icon}
          </SItemIcon>
        )}
        <SItemTitle>{title}</SItemTitle>
      </SItemButton>

      {action && isOpen && (
        <SHintTooltip
          title={action.title}
          enterDelay={ACTION_TOOLTIP_DELAY_MS}
          enterNextDelay={ACTION_TOOLTIP_DELAY_MS}
          mode="dark"
          placement="bottom-end"
          slotProps={{ popper: ACTION_TOOLTIP_POPPER_PROPS }}
        >
          <SItemAction
            aria-label={action.title}
            className={SIDEBAR_ITEM_ACTION_CLASS}
            data-testid={action.dataTestId}
            onClick={handleActionClick}
          >
            <PlusIcon height={15} width={15} />
          </SItemAction>
        </SHintTooltip>
      )}
    </SItem>
  );
};
